/* eslint-disable max-len, react/jsx-closing-bracket-location, react/jsx-closing-tag-location, react/jsx-indent, react/jsx-indent-props, react/jsx-wrap-multilines */
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import moment from 'moment';
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Skeleton,
  Fade,
  Backdrop,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
// Dashboard card iconography — Lucide (thin, consistent stroke icons).
import {
  Video,
  CloudUpload,
  Upload,
  Users,
  Clock,
  HardDrive,
  Settings,
  Sparkles,
  Smartphone,
  BookOpen,
  Crown,
  ArrowUpRight,
  ExternalLink,
  Search,
  UserPlus,
  Copy,
  Check,
  X,
  Trash2,
  Layers,
  LayoutGrid,
} from 'Utils/lucideIcons';
import { ref, off, onValue } from 'firebase/database';
import { Page, Snackbar } from 'Components';
import { ConfirmDialog } from 'Components/ConfirmDialog';
import { useDirectUpload } from 'hooks/useDirectUpload';
import { useBulkDownloadAll, DOWNLOAD_MODES } from 'hooks/useBulkDownloadAll';
import PillSearch from 'Components/PillSearch';
import { VideoPopupModal } from 'Components/VideoPopup';
import { AppContext, AuthContext } from 'Context';
import { Pathname } from 'Routes';
import { dataFetcher, endpoints, baseUrl, axios as apiAxios } from 'Api';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { useDispatch } from 'react-redux';
import { saveUploadInfo } from 'store/store';
import uploadManager from 'Utils/uploadManagerSecure';
import { ALLOWED_UPLOADS_COUNT } from 'Commons';
import ListingModal from 'Components/ListingModal';
import { useInviteLink } from 'hooks/useInviteLink';
import { UploadModal } from 'Components/upload-modal';
import VideoUploadModal from 'Components/VideoUploadModal/VideoUploadModal';
import PhotoUploadModal from 'Components/PhotoUploadModal/PhotoUploadModal';
import MobileUploadQRModal from 'Components/UploadDropdown/MobileUploadQRModal';
import CloudUploadModal from 'Components/UploadDropdown/CloudUploadModal';
import useMembership from 'Pages/userManagement/MembershipDemo/useMembership';
import { V2ThemeProvider } from '../_shared/V2Layout';
import { SpotlightCard } from '../_shared/SpotlightCard';

// Colours for the membership pill. The LABEL is always the real plan name from
// /v4/membership/my-membership (the same value /settings/manage-users prints as
// its "Your membership" heading) — this table only decides the pill's gradient
// when that plan happens to be one of the named tiers. Anything else (e.g.
// "Legacy") gets MEMBERSHIP_TIER_DEFAULT.
const MEMBERSHIP_TIERS = {
  bronze: { from: '#b06a3a', to: '#e0a878', text: '#fff' },
  silver: { from: '#8b93a1', to: '#d7dce4', text: '#20242c' },
  gold: { from: '#c99a2e', to: '#f4d371', text: '#20242c' },
  platinum: { from: '#3a4a5f', to: '#aab8ca', text: '#fff' },
};
const MEMBERSHIP_TIER_DEFAULT = { from: '#2b3242', to: '#5b6478', text: '#fff' };

// Lock a section's inner scroll until it is fully in the viewport, so scrolling
// moves the PAGE (bringing the section into view) rather than the inner list
// capturing the wheel too early. Returns [ref, unlocked]; attach ref to the
// scroll container and switch overflowY to 'auto' only when unlocked.
const useScrollUnlockWhenVisible = () => {
  const ref = useRef(null);
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    const evaluate = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // A container taller than the viewport can never be fully visible — unlock
      // once it fills the viewport so the list stays usable.
      const tallerThanViewport = rect.height > vh - 24;
      const fullyVisible = rect.top >= 0 && rect.bottom <= vh + 1;
      const fillsViewport = rect.top <= 8 && rect.bottom >= vh - 8;
      setUnlocked(tallerThanViewport ? fillsViewport : fullyVisible);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(evaluate); };
    evaluate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return [ref, unlocked];
};

// Thin drag-to-scroll indicator for long lists (an affiliate can have
// hundreds of users) — sits on the left edge of a scroll container so users
// can grab it and drag straight to the bottom instead of hunting for the
// native scrollbar. Auto-hides when the content already fits.
const MIN_THUMB_HEIGHT = 28;
// Indicator only shows up while actively scrolling/dragging, then fades out
// after this many ms of no scroll activity.
const HIDE_AFTER_IDLE_MS = 3000;

const QuickScrollbar = ({ containerRef }) => {
  const trackRef = useRef(null);
  const dragRef = useRef(null); // { startY, startScrollTop }
  const hideTimerRef = useRef(null);
  const [metrics, setMetrics] = useState({ visible: false, thumbHeight: 0, thumbTop: 0 });
  const [active, setActive] = useState(false);

  // Marks the indicator as "in use" and (re)starts the 5s countdown to hide
  // it again — called on every scroll/drag tick, so it stays visible for as
  // long as scrolling keeps happening and only fades once it truly stops.
  const revealTemporarily = useCallback(() => {
    setActive(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setActive(false), HIDE_AFTER_IDLE_MS);
  }, []);

  const recompute = useCallback(() => {
    const el = containerRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    const trackHeight = track.clientHeight;
    if (scrollHeight <= clientHeight + 1 || trackHeight <= 0) {
      setMetrics((m) => (m.visible ? { visible: false, thumbHeight: 0, thumbTop: 0 } : m));
      return;
    }
    const thumbHeight = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
    const maxThumbTop = trackHeight - thumbHeight;
    const maxScrollTop = scrollHeight - clientHeight;
    const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0;
    setMetrics({ visible: true, thumbHeight, thumbTop });
  }, [containerRef]);

  const handleScroll = useCallback(() => {
    recompute();
    revealTemporarily();
  }, [recompute, revealTemporarily]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    recompute();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', recompute);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null;
    ro?.observe(el);
    // Rows changing (search filter, data load) resizes the table inside a
    // fixed-height container, which ResizeObserver on the container itself
    // won't catch — watch the DOM for that instead.
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(recompute) : null;
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', recompute);
      ro?.disconnect();
      mo?.disconnect();
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [containerRef, recompute, handleScroll]);

  const onThumbPointerDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    revealTemporarily();
    dragRef.current = { startY: e.clientY, startScrollTop: el.scrollTop };
    const onMove = (moveEvent) => {
      const drag = dragRef.current;
      const track = trackRef.current;
      if (!drag || !track) return;
      const { scrollHeight, clientHeight } = el;
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
      const maxThumbTop = trackHeight - thumbHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxThumbTop <= 0) return;
      const scrollDelta = ((moveEvent.clientY - drag.startY) / maxThumbTop) * maxScrollTop;
      el.scrollTop = Math.min(maxScrollTop, Math.max(0, drag.startScrollTop + scrollDelta));
      revealTemporarily();
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Clicking anywhere else on the track jumps straight there.
  const onTrackPointerDown = (e) => {
    const el = containerRef.current;
    const track = trackRef.current;
    if (!el || !track || e.target !== track) return;
    const rect = track.getBoundingClientRect();
    const { scrollHeight, clientHeight } = el;
    const trackHeight = rect.height;
    const thumbHeight = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
    const maxThumbTop = trackHeight - thumbHeight;
    if (maxThumbTop <= 0) return;
    const targetTop = e.clientY - rect.top - thumbHeight / 2;
    const ratio = Math.min(1, Math.max(0, targetTop / maxThumbTop));
    el.scrollTop = ratio * (scrollHeight - clientHeight);
    revealTemporarily();
  };

  const showIndicator = metrics.visible && active;

  return (
    <Box
      ref={trackRef}
      onPointerDown={onTrackPointerDown}
      sx={{
        position: 'absolute',
        left: 2,
        top: 4,
        bottom: 4,
        width: 6,
        borderRadius: 999,
        cursor: 'pointer',
        zIndex: 2,
        // Always mounted (never `display: none`) so trackRef has a real
        // clientHeight to measure from the very first layout pass — hiding it
        // via display would leave the track height at 0 forever, which is
        // exactly why the indicator wasn't showing up before. Visibility is
        // driven by `showIndicator` (overflow present AND recently scrolled),
        // not just the overflow check.
        opacity: showIndicator ? 1 : 0,
        pointerEvents: showIndicator ? 'auto' : 'none',
        transition: 'background-color 0.15s ease, opacity 0.2s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        onPointerDown={onThumbPointerDown}
        sx={{
          position: 'absolute',
          left: 0,
          width: 6,
          top: metrics.thumbTop,
          height: metrics.thumbHeight,
          borderRadius: 999,
          bgcolor: '#c7cbd3',
          cursor: 'grab',
          transition: 'background-color 0.15s ease',
          '&:hover': { bgcolor: '#9aa1ab' },
          '&:active': { bgcolor: '#6b7280', cursor: 'grabbing' },
        }}
      />
    </Box>
  );
};

// One "people" column (Viewers / Affiliates) on the affiliate dashboard. Both
// columns render through this so they are pixel-identical: a header (title +
// subtext) with an inline search and action button(s) on the right, over a
// simple name/email user table. `actions` is the copy(+invite) block; each row
// gets an optional trailing `rowAction`.
// `titleAction` renders inline to the right of the title — that's where both
// columns keep their copy / invite buttons; the right side holds just the search.
const PeopleColumn = ({
  title, subtitle, titleAction, search, onSearch, rows, rowAction, emptyText, loading = false,
  titleNode = null, contentKey = null,
}) => {
  const [scrollRef, scrollUnlocked] = useScrollUnlockWhenVisible();
  return (
  <Card sx={{ p: { xs: 2, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          {titleNode || <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{title}</Typography>}
          {titleAction}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ alignItems: 'center' }}>
        <PillSearch value={search} onChange={onSearch} placeholder="Search" minWidth={150} />
      </Stack>
    </Stack>
    {/* Fills the card so both columns are the same height. Inner scroll stays
        locked until the whole table is in view, then switches to 'auto'. */}
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, maxHeight: 460, display: 'flex', flexDirection: 'column' }}>
      <QuickScrollbar containerRef={scrollRef} />
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: 460,
          overflowY: scrollUnlocked ? 'auto' : 'hidden',
          pl: 1.75,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
      {/* Keyed inner wrapper: when contentKey changes (tab switch) only this
          list subtree remounts and fades in, so the card frame never blinks. */}
      <Box
        key={contentKey == null ? undefined : contentKey}
        sx={contentKey == null ? undefined : {
          animation: 'peopleColumnFade 260ms cubic-bezier(0.22, 1, 0.36, 1)',
          '@keyframes peopleColumnFade': {
            from: { opacity: 0, transform: 'translateY(5px)' },
            to: { opacity: 1, transform: 'none' },
          },
        }}
      >
      {loading ? (
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {[0, 1, 2, 3].map((i) => (
            <Stack key={i} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} animation="wave" />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="45%" height={16} animation="wave" />
                <Skeleton variant="text" width="65%" height={14} animation="wave" />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : !rows.length ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>{emptyText}</Typography>
      ) : (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="tbody">
            {rows.map((row) => {
              const name = `${row.firstname || ''} ${row.lastname || ''}`.trim() || row.email?.split('@')[0] || '—';
              return (
                <Box
                  component="tr"
                  key={row.id || row.email}
                  sx={{ '& td': { py: 1, px: 0.5, verticalAlign: 'middle' }, '&:hover td': { bgcolor: 'action.hover' } }}
                >
                  <Box component="td" sx={{ width: 44 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>
                      {name.charAt(0)}
                    </Box>
                  </Box>
                  <Box component="td">
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{row.email}</Typography>
                  </Box>
                  {rowAction && (
                    <Box component="td" sx={{ textAlign: 'right', width: 44 }}>{rowAction(row, name)}</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      </Box>
      </Box>
    </Box>
  </Card>
  );
};

// Icon-button used for the PeopleColumn header actions (copy / invite) — no
// stroke, just a subtle hover fill.
const PEOPLE_ICON_BTN_SX = {
  borderRadius: '10px',
  color: '#15171c',
  '&:hover': { bgcolor: 'rgba(21,23,28,0.06)' },
};

// Nav is a bottom dock now — no left rail offset.
const SIDEBAR_OFFSET = {
  xs: 0,
  sm: 0,
  md: 0,
  lg: 0,
  xl: 0,
};

const checkSize = (size) => {
  if (size <= 0) return '0 MB';
  const bytes = size * 1048576;
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / (1024 ** i)).toFixed(2))} ${units[i]}`;
};

const changeDateFormat = (date) => {
  if (!date) return null;
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
};

// Local-file "Choose Local Files" support (mirrors the header Upload dropdown /
// the /storage uploader): split a picked set into videos vs photos.
const LOCAL_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.3gp', '.mpg', '.mpeg', '.ts', '.mts'];
const LOCAL_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif', '.tiff', '.tif', '.svg'];
const LOCAL_FILE_ACCEPT = `${[...LOCAL_VIDEO_EXTENSIONS, ...LOCAL_IMAGE_EXTENSIONS].join(',')},video/*,image/*`;

const SectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pr: 1,
    }}
  >
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

// Large stat "object" — circular icon + label + big value + subtitle, with an
// optional right-aligned action slot (used for the storage → content/layout
// shortcut icons).
const StatCard = ({
  icon: Icon, label, value, sub, accent, rightAction, compact = false,
}) => (
  <Card
    sx={{
      p: compact ? 1.5 : 2,
      display: 'flex',
      alignItems: 'center',
      gap: compact ? 0 : 1.5,
      transition: 'transform 200ms ease, box-shadow 200ms ease',
      // Hover: lift + a 1px stroke ring (box-shadow so there's no layout shift).
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 0 0 1px #d0d5dd, 0 10px 24px rgba(15,17,28,0.10)' },
      '&:hover .stat-ic': { transform: 'scale(1.08) rotate(-6deg)' },
    }}
  >
    {/* Compact tiles drop the icon — heading + value only. */}
    {!compact && (
      <Box
        className="stat-ic"
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: accent || 'action.hover',
          color: accent ? '#fff' : 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <Icon size={22} />
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11, fontWeight: 600 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: compact ? 20 : 22, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
          {sub}
        </Typography>
      )}
    </Box>
    {rightAction}
  </Card>
);

const SourceTile = ({ icon: Icon, title, description, onClick, disabled, loading, dense }) => {
  const isBusy = disabled || loading;
  return (
    <Box
      role="button"
      tabIndex={isBusy ? -1 : 0}
      onClick={isBusy ? undefined : onClick}
      onKeyDown={(e) => {
        if (!isBusy && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: dense ? 1 : 1.5,
        p: dense ? 1 : 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: loading ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        cursor: loading ? 'progress' : (disabled ? 'not-allowed' : 'pointer'),
        opacity: loading ? 1 : (disabled ? 0.5 : 1),
        transition: 'background-color 160ms ease, border-color 160ms ease, transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:hover': isBusy ? {} : {
          bgcolor: 'action.hover',
          borderColor: 'text.primary',
          transform: 'translateY(-1px)',
        },
        '&:hover .src-ic': isBusy ? {} : { transform: 'scale(1.14)', color: 'text.primary' },
        '&:active': isBusy ? {} : { transform: 'translateY(0)' },
      }}
    >
      <Box
        className="src-ic"
        sx={{
          width: dense ? 30 : 36,
          height: dense ? 30 : 36,
          borderRadius: 1.5,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          flexShrink: 0,
          transition: 'transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1), color 180ms ease',
        }}
      >
        {loading ? <CircularProgress size={dense ? 15 : 18} sx={{ color: 'primary.main' }} /> : <Icon size={dense ? 22 : 26} />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: dense ? 12.5 : 13, fontWeight: 600 }} noWrap={dense}>{title}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, fontSize: dense ? 11 : undefined }} noWrap={dense}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const DropboxBrandIcon = ({ sx, size = 24, ...rest }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...rest}
    sx={{ width: size, height: size, ...sx, color: '#0061FF' }}
  >
    <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
  </Box>
);

const GoogleDriveBrandIcon = ({ sx, size = 24, ...rest }) => (
  <Box
    component="svg"
    viewBox="0 0 87.3 78"
    {...rest}
    sx={{ width: size, height: size, ...sx }}
  >
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </Box>
);

const GooglePhotosBrandIcon = ({ sx, size = 24, ...rest }) => (
  <Box
    component="svg"
    viewBox="0 0 122.88 122.88"
    {...rest}
    sx={{ width: size, height: size, ...sx }}
  >
    <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#4285f4" />
    <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#ea4335" />
    <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#fbbc04" />
    <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34a853" />
  </Box>
);

const ApplePhotosBrandIcon = ({ sx, size = 24, ...rest }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...rest}
    sx={{ width: size, height: size, ...sx }}
  >
    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </Box>
);

// Rotating one-line "insight" under the greeting. Cross-fades through the
// supplied items every few seconds. The item list can change as data loads
// (we only reset the timer when the number of items changes, and always read
// the latest text via a ref so a re-render doesn't restart the cycle).
const RotatingInsight = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);
  const len = items?.length || 0;

  useEffect(() => {
    if (len <= 1) return undefined;
    const id = setInterval(() => {
      setShow(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % len);
        setShow(true);
      }, 300);
    }, 3800);
    return () => clearInterval(id);
  }, [len]);

  if (len === 0) return null;
  const item = items[Math.min(idx, len - 1)] || items[0];
  return (
    <Box sx={{ minHeight: 22, mt: 0.5 }}>
      <Fade in={show} timeout={300}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: 'text.secondary' }}>
          {item.icon}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {item.text}
          </Typography>
        </Stack>
      </Fade>
    </Box>
  );
};

// Shimmer placeholder shown while the subscription/content data loads — mirrors
// the real dashboard layout (upload card + right-hand stat column, then the two
// viewer cards) so the transition to real content is not a layout jump.
const DashboardSkeleton = () => (
  <Stack spacing={2}>
    {/* One full-width card so the shimmer spans the whole row — main content
        (left) and the storage side (right) — instead of leaving the storage
        column as bare skeletons floating outside the card. */}
    <Card sx={{ p: { xs: 2, md: 2.5 }, width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'stretch', gap: 2.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rounded" width={22} height={22} animation="wave" />
            <Skeleton variant="text" width={160} height={26} animation="wave" />
          </Stack>
          <Skeleton variant="rounded" width={200} height={44} animation="wave" sx={{ mb: 3 }} />
          <Skeleton variant="text" width={120} height={18} animation="wave" sx={{ mb: 1.25 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={54} animation="wave" />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Skeleton variant="text" width={90} height={22} animation="wave" />
          <Skeleton variant="rounded" height={10} animation="wave" sx={{ my: 1.5, borderRadius: 5 }} />
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            flexShrink: 0,
            width: { xs: '100%', lg: 260 },
            display: 'grid',
            gridTemplateRows: { xs: 'auto', lg: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={78} animation="wave" sx={{ height: '100%', minHeight: 78 }} />
          ))}
        </Box>
      </Box>
    </Card>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="text" width={140} height={24} animation="wave" />
            <Skeleton variant="rounded" width={96} height={30} animation="wave" />
          </Stack>
          {Array.from({ length: 3 }).map((__, j) => (
            <Stack key={j} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
              <Skeleton variant="circular" width={36} height={36} animation="wave" />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={18} animation="wave" />
                <Skeleton variant="text" width="40%" height={14} animation="wave" />
              </Box>
            </Stack>
          ))}
        </Card>
      ))}
    </Box>
  </Stack>
);

const DashboardInner = () => {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const location = useLocation();
  const navigate = useNavigate();
  const {
    userDetails, firebaseDatabase, subscriptionDetails, langText,
    setViewerListUpdate, isViewerListUpdated, getSubscription, setTopOrBottom,
    autoSignIn, setHelpNeed, setNeedToShowETA, uploaderLoader, setUploaderLoader,
    draftcontent, holdWhileDelete, openNewUploadModal, setOpenNewUploadModal,
    openPhotoUploadModal, setOpenPhotoUploadModal, trailVideos,
    setTrailPopUpModalVisible, draftOfUpload, totalDraft, freePlanLimitMb,
    apiResponse, userAccessedData, managerPrivatePlanAccess, setAppSnackbar,
    switchingFunction, userConnections, getUserConnections,
  } = useContext(AppContext);
  const { isInActiveUser, gettingSubscription, getToken } = useContext(AuthContext);
  // "Download all" — shared with /your-videos; see hooks/useBulkDownloadAll.
  const {
    downloadAllContent, preparingDownload, bulkDownloadProgress,
    downloadMenuAnchor, setDownloadMenuAnchor,
  } = useBulkDownloadAll();

  const dispatch = useDispatch();
  const contentDashboard = langText?.contentDashboard;

  const [remainingDays, setRemainingDays] = useState(null);
  const [popUpModalVisible, setPopUpModalVisible] = useState(false);
  const [storageDetails, setStorageDetails] = useState({
    max: 0,
    used: 0,
    available: 0,
    availablePercentage: 100,
    usedPercentage: 0,
    usedVideoStorage: 0,
    usedPhotoStorage: 0,
    usedVideoPercentage: 0,
    usedPhotoPercentage: 0,
    allowedViewers: '',
    viewer: 0,
    planName: '',
    upgradePlanName: '',
    upgradePlanViewers: '',
    upgradePlanStorage: '',
  });
  const [, setStorageFull] = useState(false);
  const isStorageFullCondition = parseInt(subscriptionDetails?.storageUsed?.used_storage, 10)
    === parseInt(subscriptionDetails?.storageUsed?.total_storage, 10);
  const totalDataUploaded = (draftOfUpload?.length || 0) + (totalDraft?.length || 0);
  const isHeTrial10 = subscriptionDetails?.subscription?.is_trial === '1' && totalDataUploaded === 10;

  const getDateDifference = (endDate) => {
    const from = moment(new Date());
    const to = moment(changeDateFormat(endDate));
    return from.diff(to, 'days');
  };

  const calculations = async () => {
    if (subscriptionDetails?.storageUsed && userDetails?.totalContentUploaded) {
      const usedStorage = Math.ceil(subscriptionDetails?.storageUsed?.used_storage);
      const maxStorage = parseInt(subscriptionDetails?.storageUsed?.total_storage, 10);
      const availableStorage = maxStorage - usedStorage;
      const usedStoragePercentage = (Math.ceil((usedStorage / maxStorage) * 100) > 2)
        || (Math.ceil((usedStorage / maxStorage) * 100) === 0)
        ? Math.ceil((usedStorage / maxStorage) * 100) : 2;
      const availableStoragePercentage = (Math.floor((availableStorage / maxStorage) * 100) < 98)
        || (Math.floor((availableStorage / maxStorage) * 100) === 100)
        ? Math.floor((availableStorage / maxStorage) * 100) : 98;
      const usedVideoStorage = Math.ceil(subscriptionDetails?.storageUsed?.video_storage);
      const usedPhotoStorage = Math.ceil(subscriptionDetails?.storageUsed?.album_storage);
      const usedVideoPercentage = (Math.ceil((usedVideoStorage / maxStorage) * 100) > 2)
        || (Math.ceil((usedVideoStorage / maxStorage) * 100) === 0)
        ? Math.ceil((usedVideoStorage / maxStorage) * 100) : 2;
      const usedPhotoPercentage = (Math.ceil((usedPhotoStorage / maxStorage) * 100) > 2)
        || (Math.ceil((usedPhotoStorage / maxStorage) * 100) === 0)
        ? Math.ceil((usedPhotoStorage / maxStorage) * 100) : 2;

      setStorageDetails({
        max: checkSize(maxStorage),
        used: checkSize(usedStorage),
        available: checkSize(availableStorage),
        availablePercentage: availableStoragePercentage,
        usedPercentage: usedStoragePercentage,
        usedVideoStorage: checkSize(usedVideoStorage),
        usedPhotoStorage: checkSize(usedPhotoStorage),
        usedVideoPercentage,
        usedPhotoPercentage,
        allowedViewers: subscriptionDetails?.subscription?.is_unlimited === '1'
          ? 'Unlimited'
          : subscriptionDetails?.subscription?.max_view_request || null,
        viewer: subscriptionDetails?.total_viewers,
        planName: subscriptionDetails?.subscription?.product_name,
        upgradePlanName: subscriptionDetails?.is_addon_allowed
          ? subscriptionDetails?.action
          : subscriptionDetails?.upgradePlanDetails?.[0]?.product_name || null,
        upgradePlanViewers: subscriptionDetails?.is_addon_allowed
          ? (subscriptionDetails?.subscription?.is_unlimited === '1' ? 'Unlimited' : subscriptionDetails?.subscription?.max_view_request)
          : (subscriptionDetails?.upgradePlanDetails?.[0]?.is_unlimited === '1' ? 'Unlimited' : subscriptionDetails?.upgradePlanDetails?.[0]?.max_view_request) || null,
        upgradePlanStorage: subscriptionDetails?.is_addon_allowed
          ? checkSize(parseFloat(subscriptionDetails?.subscription?.total_storage) + parseFloat(subscriptionDetails?.subscription?.max_add_on_allowed))
          : subscriptionDetails?.upgradePlanDetails?.[0]?.storage
            ? checkSize(parseFloat(subscriptionDetails?.upgradePlanDetails?.[0]?.storage))
            : null,
      });
    }
    if (subscriptionDetails?.benefit_days && subscriptionDetails?.billing_start_date) {
      setRemainingDays(parseInt(subscriptionDetails?.benefit_days, 10) - getDateDifference(subscriptionDetails?.billing_start_date?.split(' ')[0]));
    }
    if (isStorageFullCondition) setStorageFull(true);
  };

  useEffect(() => { calculations(); }, [subscriptionDetails]);

  useEffect(() => {
    setHelpNeed(false);
    return () => setHelpNeed(true);
  }, []);

  const [accessRequests, setAccessRequests] = useState([]);
  const [, setHasAccess] = useState([]);
  const [snackbar, setSnackbar] = useState({ isVisible: false, message: '' });
  const [pendingViewersList, setPendingViewersList] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [showListingModal, setShowListingModal] = useState(false);
  const [showVideoDetailModal, setShowVideoDetailModal] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [singleVideoFile, setSingleVideoFile] = useState(null);
  // "Choose Local Files" (merged Upload videos/photos) local-file picker state.
  const localFileInputRef = useRef(null);
  const photoAlbumIdRef = useRef(null);
  // Firebase fires an immediate snapshot on attach; skip it so the viewer lists
  // aren't refetched right after the mount effect already loaded them.
  const friendReqFirstSnapshotRef = useRef(true);
  const [mobileQROpen, setMobileQROpen] = useState(false);
  const [cloudService, setCloudService] = useState(null);
  // Which upload source is mid-click — drives a per-tile/button spinner while
  // the async limit-check runs / the flow opens (mirrors the old uploader UI).
  const [loadingKey, setLoadingKey] = useState(null);

  useSWR([endpoints.getAllViewRequest], {
    fetcher: (url, status) => dataFetcher(url, { status }),
    onSuccess: ({ success, data }) => {
      if (success && data) {
        setAccessRequests(data?.filter(({ status }) => status === '0'));
        setHasAccess(data?.filter(({ status }) => status === '1'));
      } else setAccessRequests([]);
    },
    onError: () => setAccessRequests([]),
  });

  const { mutate: fetchPendingList } = useSWR([endpoints.getPendingViewersList], {
    fetcher: (url, search, status) => dataFetcher(url, { search, status }),
    onSuccess: ({ success, data }) => {
      if (success && data) setPendingViewersList(data);
      else setPendingViewersList([]);
    },
    onError: () => setPendingViewersList([]),
  });

  const { mutate: fetchViewerList } = useSWR([endpoints.getViewersList], {
    fetcher: (url, search, status) => dataFetcher(url, { search, status }),
    onSuccess: ({ success, data }) => {
      if (success && data) setViewers(data);
      else setViewers([]);
    },
    onError: () => setViewers([]),
  });

  useEffect(() => {
    setNeedToShowETA(true);
    fetchPendingList();
    fetchViewerList();
  }, []);

  useEffect(() => {
    if (isViewerListUpdated) {
      fetchPendingList();
      fetchViewerList();
      setViewerListUpdate(false);
    }
  }, [isViewerListUpdated]);

  useEffect(() => {
    if (!localStorage.getItem('id')) {
      autoSignIn();
    } else {
      const databaseRefFetchRequests = ref(firebaseDatabase, `friend_requests_updates/${localStorage.getItem('id')}`);
      const dataCallbackReq = (snapshot) => {
        if (friendReqFirstSnapshotRef.current) {
          friendReqFirstSnapshotRef.current = false;
          return;
        }
        if (snapshot.exists()) {
          fetchPendingList();
          fetchViewerList();
        }
      };
      onValue(databaseRefFetchRequests, dataCallbackReq);
      return () => off(databaseRefFetchRequests, 'value', dataCallbackReq);
    }
  }, []);

  useEffect(() => {
    // Subscription + profile are already fetched and cached by the app shell at
    // sign-in — only refetch here if the cached value is actually missing, so a
    // warm dashboard visit doesn't re-run two (one heavy) identity calls.
    if (!subscriptionDetails) getSubscription();
    if (!userDetails?.email) autoSignIn();
    setTopOrBottom(true);

    if (location.state?.videoFiles && location.state?.showModal) {
      setVideoFiles(location.state.videoFiles);
      setShowListingModal(true);
    } else if (location.state?.videoFiles && location.state?.showVideoDetailModal) {
      setSingleVideoFile(location.state.videoFiles[0]);
      setShowVideoDetailModal(true);
    }
    return () => setTopOrBottom(false);
  }, []);

  useEffect(() => {
    if (location.state?.videoFiles) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const hidepopModal = () => setPopUpModalVisible(false);

  // Photo upload — direct pipeline into the global upload widget, identical to
  // the /storage uploader (uploads to the "All Photos" album via uploadManager,
  // no metadata modal).
  const updateUploadPercentages = (albumId, data) => {
    dispatch(saveUploadInfo({ [albumId]: data }));
  };

  const blobToBase64 = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  const startPhotoAlbumUpload = async (imageFiles) => {
    let albumId = photoAlbumIdRef.current;
    if (!albumId) {
      try {
        const albumListResponse = await apiAxios.post(`${baseUrl}/getAlbumList`, {});
        const existingAlbums = albumListResponse.data?.data || [];
        const allPhotosAlbum = existingAlbums.find((a) => a.title === 'All Photos');
        if (allPhotosAlbum) albumId = allPhotosAlbum.album_id || allPhotosAlbum.id;
      } catch (err) {
        console.warn('[DashboardUpload] Failed to fetch album list, will create new album:', err);
      }
      if (!albumId) {
        const response = await apiAxios.post(`${baseUrl}/addEditAlbum`, {
          title: 'All Photos',
          description: '',
          visibility: '2',
          group_id: '',
          token: getToken(),
        });
        albumId = response.data?.album_id;
        if (!albumId) {
          console.error('[DashboardUpload] Failed to create album');
          return;
        }
      }
      photoAlbumIdRef.current = albumId;
    }

    const totalPhotos = imageFiles.length;
    const iconBase64 = await blobToBase64(imageFiles[0]);

    uploadManager.createPhotoUpload({
      albumId,
      albumTitle: 'Photo Album',
      photoCount: totalPhotos,
      updateUploadPercentages,
    });
    updateUploadPercentages(albumId, `0/${totalPhotos}`);

    let successfulUploadCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (let i = 0; i < imageFiles.length; i += 1) {
      try {
        const formData = new FormData();
        formData.append('album_id', albumId);
        formData.append('visibility', '2');
        formData.append('group_id', '');
        if (i === 0 && iconBase64) formData.append('icon', iconBase64);
        formData.append('token', getToken());
        formData.append('photo_file[]', imageFiles[i], imageFiles[i].name);
        // eslint-disable-next-line no-await-in-loop
        await apiAxios.post(`${baseUrl}/addPhotoContent`, formData);
        successfulUploadCount += 1;
        updateUploadPercentages(albumId, `${successfulUploadCount}/${totalPhotos}`);
        uploadManager.updatePhotoUploadProgress(albumId, successfulUploadCount, totalPhotos);
      } catch (error) {
        console.error(`[DashboardUpload] Error uploading photo ${i + 1}:`, error);
      }
    }

    updateUploadPercentages(albumId, null);
    uploadManager.completePhotoUpload(albumId);
    setAppSnackbar({
      isVisible: true,
      type: 'success',
      message: `Uploaded ${successfulUploadCount} ${successfulUploadCount === 1 ? 'photo' : 'photos'}`,
    });
  };

  // Same "choose local files" pipeline as /storage/videos (direct upload
  // manager -> global UploadPanel), instead of the old modal flow.
  const directUpload = useDirectUpload();

  // When files are chosen from the dashboard, dim/blur the screen while the
  // upload kicks off, then hand the user over to the /storage/videos upload
  // manager where they can watch progress.
  const [startingUpload, setStartingUpload] = useState(false);
  const handleDashboardFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setStartingUpload(true);
    directUpload.handleFileSelect(event);
    setTimeout(() => {
      navigate(Pathname.getStoragePath(Pathname.storage.videos));
    }, 900);
  };

  // "Choose Local Files" — one entry point for both videos and photos (mirrors
  // the /storage uploader tile). Runs the same free-plan/trial gate as the
  // video upload, then opens the OS file picker.
  const openLocalFilePicker = () => {
    setUploaderLoader(true);
    setLoadingKey('video');
    localStorage.setItem('opened_draft_id', null);
    autoSignIn().then((res) => {
      if (!res?.data?.subscription && res?.data?.totalContentUploaded >= ALLOWED_UPLOADS_COUNT) {
        setTrailPopUpModalVisible(true);
      } else if ((subscriptionDetails?.subscription?.is_trial === '1' && trailVideos === 0) || isHeTrial10) {
        setTrailPopUpModalVisible(true);
      } else {
        localFileInputRef.current?.click();
      }
    }).finally(() => {
      setUploaderLoader(false);
      setLoadingKey(null);
    });
  };

  // Split the picked files into videos vs photos and route each to its existing
  // upload flow: videos → the ListingModal, photos → the photo UploadModal.
  const handleLocalFilesSelect = (event) => {
    const inputElement = event.target;
    const files = Array.from(inputElement.files || []);
    inputElement.value = ''; // allow re-selecting the same files
    if (files.length === 0) return;

    const pickedVideos = [];
    const pickedImages = [];
    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (LOCAL_VIDEO_EXTENSIONS.includes(ext)) pickedVideos.push(file);
      else if (LOCAL_IMAGE_EXTENSIONS.includes(ext)) pickedImages.push(file);
    });

    if (pickedVideos.length === 0 && pickedImages.length === 0) {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Unsupported file type. Please choose videos or photos.',
      });
      return;
    }

    if (pickedVideos.length > 0) {
      setVideoFiles(pickedVideos);
      setShowListingModal(true);
    }
    if (pickedImages.length > 0) {
      startPhotoAlbumUpload(pickedImages);
    }
  };

  // Tell Your Story — free-plan limit check (same as the other upload
  // options), then navigate to /gift-content with autoRecord=true so the
  // "Tell Your Story" recorder overlay auto-opens; fromNormalApp=true returns
  // the user to /your-videos when they finish. Mirrors the classic uploader.
  const handleTellYourStory = () => {
    const goToRecorder = () => {
      const base = Pathname.giftFlow.content;
      const sep = base.includes('?') ? '&' : '?';
      navigate(`${base}${sep}autoRecord=true&fromNormalApp=true`);
    };
    setUploaderLoader(true);
    setLoadingKey('story');
    autoSignIn()
      .then((res) => {
        setUploaderLoader(false);
        setLoadingKey(null);
        if (!res?.data?.subscription && res?.data?.totalContentUploaded >= ALLOWED_UPLOADS_COUNT) {
          setTrailPopUpModalVisible(true);
          return;
        }
        goToRecorder();
      })
      .catch(() => {
        // If the limit check fails, don't block the user — go to the recorder.
        setUploaderLoader(false);
        setLoadingKey(null);
        goToRecorder();
      });
  };

  const uploadDisabled = draftcontent?.length > 0 || uploaderLoader === true || holdWhileDelete === true;
  // "Hard" block (active draft / delete in progress) → buttons should grey out.
  // Being disabled only because an upload is loading (uploaderLoader, which can
  // be triggered from the nav dropdown too) should NOT grey/black them out.
  const uploadHardBlocked = draftcontent?.length > 0 || holdWhileDelete === true;
  const uploadLoadingOnly = uploadDisabled && !uploadHardBlocked;
  // private_plan_access gate disabled for now — cloud options shown for all users (same as old UI).
  // Was: apiResponse?.data?.[0]?.private_plan_access === '1' || apiResponse?.data?.[0]?.private_plan_access === 1 || (userAccessedData && managerPrivatePlanAccess)
  const showCloudOptions = true;

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const firstName = userDetails?.firstname?.trim() || '';

  const isInactiveAttention = isInActiveUser
    && ((subscriptionDetails?.subscription === null && subscriptionDetails?.expired)
      || subscriptionDetails?.plans?.[subscriptionDetails?.plans?.length - 1]?.is_trial === '0');

  // While the attention overlay is up, the dashboard behind it is blurred and
  // frozen — lock body scroll so there's no stray scrollbar behind the object.
  useEffect(() => {
    if (!isInactiveAttention) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isInactiveAttention]);

  // --- Plan summary (name + renewal) shown in the header pill and the Storage
  // section, replacing the unhelpful "Unlimited Storage" label. Renewal state
  // mirrors the billing screen (renews / trial ends / canceled / expired).
  const isTrialPlan = subscriptionDetails?.subscription?.is_trial === '1';
  const isCancelledPlan = subscriptionDetails?.subscription?.is_cancelled === '1';
  const planName = subscriptionDetails?.subscription?.product_name
    || (isTrialPlan ? 'Free trial' : 'Free plan');
  const endDateRaw = subscriptionDetails?.subscription?.end_date;
  const renewalMoment = endDateRaw ? moment(endDateRaw.split(' ')[0], 'YYYY-MM-DD') : null;
  const renewalText = (() => {
    if (!renewalMoment || !renewalMoment.isValid()) return null;
    const d = renewalMoment.format('MMM D, YYYY');
    if (isCancelledPlan) return `Expires ${d}`;
    if (isTrialPlan) return `Trial ends ${d}`;
    if (subscriptionDetails?.expired) return `Expired ${d}`;
    return `Renews ${d}`;
  })();
  const planSubtitle = renewalText ? `${planName} · ${renewalText}` : planName;

  // Rotating insight lines under the greeting. Some are backed by real data;
  // "Recent uploads" and "Viewer views" have no data source in the API yet, so
  // they render as clearly-labelled "coming soon" placeholders for now.
  // TODO(api): to make these real the subscription/content payload needs
  //   (a) a count of uploads within a recent window (e.g. uploads_last_3_days)
  //   (b) per-viewer/per-video view counts (e.g. total_views + top_video).
  const insightItems = useMemo(() => {
    const items = [{ icon: <Sparkles size={15} />, text: "Here's your content overview." }];
    const videoCount = userDetails?.totalVideoUploaded || 0;
    const photoCount = userDetails?.totalPhotoUploaded || 0;
    if (videoCount || photoCount) {
      items.push({
        icon: <Video size={15} />,
        text: `${videoCount} video${videoCount === 1 ? '' : 's'} · ${photoCount} photo album${photoCount === 1 ? '' : 's'}`,
      });
    }
    if (storageDetails.used && storageDetails.used !== '0 MB') {
      items.push({
        icon: <HardDrive size={15} />,
        text: `${storageDetails.used} used of ${storageDetails.max}`,
      });
    }
    items.push({ icon: <Crown size={15} />, text: planSubtitle });
    // Placeholders — no data source yet (see TODO above).
    items.push({ icon: <Upload size={15} />, text: 'Recent upload activity — coming soon' });
    items.push({ icon: <Sparkles size={15} />, text: 'Viewer watch insights — coming soon' });
    return items;
  }, [userDetails?.totalVideoUploaded, userDetails?.totalPhotoUploaded, storageDetails.used, storageDetails.max, planSubtitle]);

  // Viewers table: which set to show.
  const [viewerFilter, setViewerFilter] = useState('active');
  // Top-level tab for the viewers card: 'viewers' = people who view my projector,
  // 'viewing' = projectors I can view (the /access connections, userConnections).
  const [viewerTab, setViewerTab] = useState('viewers');
  const [viewerSearch, setViewerSearch] = useState('');
  // Connections I can view (same data /access uses); fetch on mount if empty.
  const viewingList = (userConnections || []).filter((c) => c?.firstname !== 'Tutorials');
  useEffect(() => {
    if (getUserConnections && !(userConnections && userConnections.length)) getUserConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Approved affiliates get a referral_key; their dashboard shows the managed
  // Users table alongside the Viewers table (two columns).
  // Same source /settings/manage-users uses for its "Your membership" heading:
  // GET /v4/membership/my-membership → membership.plan_name (e.g. "Legacy").
  const { membership, loading: membershipLoading } = useMembership();
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [affiliateKey, setAffiliateKey] = useState('');
  const [affiliateBusiness, setAffiliateBusiness] = useState('');
  const [affiliateUsers, setAffiliateUsers] = useState([]);
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [affiliateLinkCopied, setAffiliateLinkCopied] = useState(false);
  // The referral + managed-users calls are async; without this the affiliate
  // tiles briefly render "0 total users / No referred users yet" before the
  // list arrives. Keep it true until both calls settle.
  const [affiliateLoading, setAffiliateLoading] = useState(true);
  // Access confirmation: pressing "Access" opens a confirm dialog; on confirm we
  // await the (async) account switch with a spinner instead of switching blind.
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const confirmAccess = async () => {
    if (!accessTarget?.row) return;
    setAccessing(true);
    try {
      await switchingFunction(accessTarget.row);
      // switchingFunction navigates to /dashboard for the accessed account.
    } catch (e) { /* switchingFunction surfaces its own errors */ } finally {
      setAccessing(false);
      setAccessTarget(null);
    }
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await apiAxios.post(`${baseUrl}/getMyReferralDetails`, { token: getToken() });
        const key = data?.referral_details?.referral_key;
        if (mounted && data?.success && key) {
          setIsAffiliate(true);
          setAffiliateKey(key);
          setAffiliateBusiness(
            data?.referral_details?.business_name || data?.referral_details?.company_name || '',
          );
          // Affiliate's users = accounts they created / can access
          // (managerUsersList) — these carry real plans + an access token, so
          // the plan split is accurate and rows get an Access button.
          try {
            const mgr = await apiAxios.post(`${baseUrl}/managerUsersList`, { token: getToken() });
            if (mounted) setAffiliateUsers(Array.isArray(mgr?.data?.data) ? mgr.data.data : []);
          } catch (mgrErr) {
            if (mounted) setAffiliateUsers([]);
          }
        }
      } catch (e) { /* not an affiliate */ } finally {
        if (mounted) setAffiliateLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const affiliateLink = affiliateKey ? `https://www.projectorstream.com/referral/${affiliateKey}` : '';
  const copyAffiliateLink = async () => {
    if (!affiliateLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(affiliateLink);
      } else {
        const ta = document.createElement('textarea');
        ta.value = affiliateLink;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setAffiliateLinkCopied(true);
      setTimeout(() => setAffiliateLinkCopied(false), 1600);
      setSnackbar({ isVisible: true, type: 'success', message: 'Affiliate link copied to clipboard' });
    } catch (e) { /* noop */ }
  };

  // Shared with the Viewers page header's Copy Invite Link button.
  const { copyInviteLink } = useInviteLink();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCancelInvite = (email) => {
    dataFetcher(endpoints.cancelInvitation, { email })
      .then((res) => {
        if (res?.success) {
          fetchPendingList();
          setSnackbar({ isVisible: true, type: 'success', message: 'Invitation cancelled' });
        } else {
          setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
        }
      })
      .catch(() => setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' }));
  };

  const handleDeleteViewer = (id, name) => {
    dataFetcher(endpoints.deleteMyViewer, { id })
      .then((res) => {
        if (res?.success) {
          fetchViewerList();
          getSubscription();
          setSnackbar({ isVisible: true, type: 'success', message: `${name || 'Viewer'} removed` });
        } else {
          setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
        }
      })
      .catch(() => setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' }));
  };

  // Copies the same /viewer/invite/:publicId link as the Viewers page header,
  // with the same messages — this used to copy the profile link instead.
  const copyProjectorLink = async () => {
    const copied = await copyInviteLink();
    if (copied) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1000);
      setSnackbar({ isVisible: true, message: 'Invite link copied to clipboard!' });
    } else {
      setSnackbar({
        isVisible: true,
        type: 'error',
        message: 'Could not copy the link. Please copy it manually.',
      });
    }
  };

  return (
    <Page contentClassName="" headerTheme="" slimV2>
      <Backdrop
        open={startingUpload}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'rgba(8,9,13,0.55)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Starting your upload…</Typography>
      </Backdrop>
      <Box
        sx={{
          minHeight: '100vh',
          // Transparent in light mode so the Page soft-aurora shows through;
          // keep a solid base in dark mode.
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.default : 'transparent'),
          color: 'text.primary',
          pl: SIDEBAR_OFFSET,
          // Pill (top) + dock (bottom) clearance is provided by Page's slimV2
          // content padding now, so every page clears them uniformly.
          pt: 1,
          pb: 1,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1320, mx: 'auto' }}>
          {/* Greeting + plan pill */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 2, alignItems: { sm: 'flex-end' }, justifyContent: 'space-between' }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, fontSize: { xs: 24, sm: 28, md: 32 }, letterSpacing: '-0.02em' }}
              >
                {greeting}
                {firstName ? `, ${firstName}` : ''}
              </Typography>
              <RotatingInsight items={insightItems} />
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
              {remainingDays && remainingDays > 0 && (
                <Chip
                  icon={<Sparkles size={14} style={{ marginLeft: 8 }} />}
                  label={`${remainingDays} free days left`}
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {/* Plan pill — name + renewal date + upgrade CTA */}
              {/* <Card
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.75,
                  pl: 1.5,
                  pr: 0.75,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Crown size={15} color={isPaidPlan ? '#eab308' : undefined} />
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.2 }} noWrap>
                      {planName}
                    </Typography>
                  </Stack>
                  {renewalText && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                      {renewalText}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  onClick={() => navigate(Pathname.plans)}
                  endIcon={<ArrowUpRight size={15} />}
                  sx={{ whiteSpace: 'nowrap', borderRadius: 2 }}
                >
                  Upgrade
                </Button>
              </Card> */}
            </Stack>
          </Stack>

          {/* Hidden modals — preserve all original wiring */}
          <VideoPopupModal isVisible={popUpModalVisible} onHide={hidepopModal} />
          <VideoUploadModal
            open={openNewUploadModal}
            onClose={() => setOpenNewUploadModal(false)}
            userDataProp={{
              subscription: userDetails?.isSubscription || null,
              totalContentUploaded: userDetails?.totalContentUploaded || 0,
            }}
          />
          <PhotoUploadModal
            open={openPhotoUploadModal}
            onClose={() => setOpenPhotoUploadModal(false)}
            userData={{
              subscription: userDetails?.isSubscription || null,
              totalContentUploaded: userDetails?.totalContentUploaded || 0,
            }}
          />

          {!subscriptionDetails ? (
            <DashboardSkeleton />
          ) : (
            <Box sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              {/* Merged box + stat tiles side by side. When expanded, the tiles
                  slide into a fixed right column and stretch to the box height;
                  when collapsed the merged box takes the full width. */}
              {/* Main card: upload + sources on the left, storage split on the right. */}
              <SpotlightCard sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.3fr) minmax(0, 1fr)' },
                    gap: { xs: 3, lg: 4 },
                    alignItems: 'start',
                  }}
                >
                  {/* LEFT: upload + other sources. Header sits at the top of the
                      column so it aligns with the Storage header on the right. */}
                  <Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                      <CloudUpload size={20} />
                      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Upload content</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Add videos or photos to share with your viewers.
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      <input
                        ref={localFileInputRef}
                        type="file"
                        multiple
                        accept={LOCAL_FILE_ACCEPT}
                        style={{ display: 'none' }}
                        onChange={handleDashboardFileSelect}
                      />
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={loadingKey === 'video' ? <CircularProgress size={16} sx={{ color: 'primary.contrastText' }} /> : null /* <Upload size={18} /> */}
                        onClick={openLocalFilePicker}
                        disabled={uploadDisabled}
                        sx={{
                          borderRadius: 2,
                          ...(loadingKey === 'video'
                            ? { '&.Mui-disabled': { color: 'primary.contrastText', opacity: 1 } }
                            : uploadLoadingOnly
                              ? { '&.Mui-disabled': { bgcolor: 'primary.main', color: 'primary.contrastText', opacity: 1 } }
                              : {}),
                        }}
                      >
                        Choose Local Files
                      </Button>
                    </Stack>
                    {uploadDisabled && draftcontent?.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'warning.main' }}>
                        Finish or discard your active draft to start a new upload.
                      </Typography>
                    )}
                    <Typography
                      variant="overline"
                      sx={{ color: 'text.secondary', letterSpacing: 0.6, display: 'block', mb: 1.25, mt: 3 }}
                    >
                      Other sources
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1,
                      }}
                    >
                      <SourceTile
                        dense
                        icon={BookOpen}
                        title="Tell Your Story"
                        description="Record a personal video message"
                        onClick={handleTellYourStory}
                        disabled={uploadDisabled}
                        loading={loadingKey === 'story'}
                      />
                      <SourceTile
                        dense
                        icon={Smartphone}
                        title="From phone"
                        description="Scan a QR to upload from your mobile"
                        onClick={() => setMobileQROpen(true)}
                        disabled={uploadDisabled}
                      />
                      <SourceTile
                        dense
                        icon={ApplePhotosBrandIcon}
                        title="Apple Photos"
                        description="Upload from your iPhone photo library"
                        onClick={() => setMobileQROpen(true)}
                        disabled={uploadDisabled}
                      />
                      {showCloudOptions && (
                        <>
                          <SourceTile
                            dense
                            icon={GooglePhotosBrandIcon}
                            title="Google Photos"
                            description="Pick photos from Google"
                            onClick={() => setCloudService('googledrive')}
                            disabled={uploadDisabled}
                          />
                          <SourceTile
                            dense
                            icon={DropboxBrandIcon}
                            title="From Dropbox"
                            description="Pick files from your Dropbox"
                            onClick={() => setCloudService('dropbox')}
                            disabled={uploadDisabled}
                          />
                          <SourceTile
                            dense
                            icon={GoogleDriveBrandIcon}
                            title="From Google Drive"
                            description="Pick files from your Drive"
                            onClick={() => setCloudService('googledrive')}
                            disabled={uploadDisabled}
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* RIGHT: storage split */}
                  <Box sx={{ pl: { lg: 3.5 }, borderLeft: { lg: '1px solid' }, borderColor: { lg: 'divider' } }}>
                  <SectionHeader
                    title="Storage"
                    subtitle={planName}
                    action={(
                      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                        <Tooltip title={!preparingDownload
                          ? 'Download all content'
                          : bulkDownloadProgress.total > 0
                            ? `Downloading ${bulkDownloadProgress.current}/${bulkDownloadProgress.total}`
                            : 'Downloading…'}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                              disabled={preparingDownload}
                            >
                              {preparingDownload
                                ? <CircularProgress size={18} color="inherit" />
                                : <DownloadRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Menu
                          anchorEl={downloadMenuAnchor}
                          open={Boolean(downloadMenuAnchor)}
                          onClose={() => setDownloadMenuAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          PaperProps={{ sx: { mt: 1, borderRadius: 2, minWidth: 220 } }}
                        >
                          {DOWNLOAD_MODES.map(({ mode, label, Icon }) => (
                            <MenuItem
                              key={mode}
                              onClick={() => {
                                setDownloadMenuAnchor(null);
                                // Stays on the click path — the folder picker
                                // needs the user gesture.
                                downloadAllContent(mode);
                              }}
                              sx={{ fontSize: 14, fontWeight: 600, py: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 30 }}><Icon size={17} /></ListItemIcon>
                              {label}
                            </MenuItem>
                          ))}
                        </Menu>
                        <Tooltip title="Go to Content">
                          <IconButton size="small" onClick={() => navigate(Pathname.yourVideos)}>
                            <Layers size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Go to Layout">
                          <IconButton size="small" onClick={() => navigate(Pathname.contentLayout)}>
                            <LayoutGrid size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Storage settings">
                          <IconButton size="small" onClick={() => navigate(Pathname.getSettingsPath(Pathname.settings.billings))}>
                            <Settings size={18} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                />
                  {/* Total / Videos / Albums — one compact row (no icons). */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1 }}>
                    <StatCard
                      compact
                      label="Total"
                      value={storageDetails.max || '—'}
                      sub={`${storageDetails.used} used`}
                    />
                    <StatCard
                      compact
                      label="Videos"
                      value={userDetails?.totalVideoUploaded || 0}
                      sub={`${storageDetails.usedVideoStorage} used`}
                    />
                    <StatCard
                      compact
                      label="Albums"
                      value={userDetails?.totalPhotoUploaded || 0}
                      sub={`${storageDetails.usedPhotoStorage} used`}
                    />
                  </Box>

                  {/* Affiliates: business snapshot lives in this same section. */}
                  {isAffiliate && (() => {
                    // Real plan name from /v4/membership/my-membership. No plan
                    // (or still loading) → no pill at all, rather than a
                    // placeholder tier.
                    // (named membershipPlan, not planName — an outer planName
                    // already holds the SUBSCRIPTION product name.)
                    const membershipPlan = membership?.plan_name || '';
                    const tier = MEMBERSHIP_TIERS[membershipPlan.toLowerCase()] || MEMBERSHIP_TIER_DEFAULT;
                    const planCounts = affiliateUsers.reduce((acc, u) => {
                      const p = (u?.subscription?.product_name || u?.plan_name || 'Free').toString();
                      acc[p] = (acc[p] || 0) + 1;
                      return acc;
                    }, {});
                    const planTiles = Object.entries(planCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);
                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 15 }} noWrap>{affiliateBusiness || 'Affiliates'}</Typography>
                              <Tooltip title={affiliateLinkCopied ? 'Copied' : 'Copy affiliate link'}>
                                <IconButton size="small" onClick={copyAffiliateLink} sx={{ color: 'text.secondary' }}>
                                  {affiliateLinkCopied ? <Check size={16} /> : <Copy size={16} />}
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            {affiliateLoading ? (
                              <Skeleton variant="text" width={90} height={16} animation="wave" />
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {`${affiliateUsers.length} total ${affiliateUsers.length === 1 ? 'user' : 'users'}`}
                              </Typography>
                            )}
                          </Box>
                          {!membershipLoading && membershipPlan ? (
                            <Tooltip title={`${membershipPlan}${membership?.billing_title ? ` · ${membership.billing_title}` : ''} membership`}>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                sx={{
                                  alignItems: 'center',
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: 999,
                                  flexShrink: 0,
                                  background: `linear-gradient(135deg, ${tier.from}, ${tier.to})`,
                                  color: tier.text,
                                }}
                              >
                                <Crown size={14} />
                                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.3 }}>{membershipPlan}</Typography>
                              </Stack>
                            </Tooltip>
                          ) : null}
                        </Stack>
                        {affiliateLoading ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                            {[0, 1, 2].map((i) => (
                              <Skeleton key={i} variant="rounded" height={64} animation="wave" />
                            ))}
                          </Box>
                        ) : planTiles.length > 0 ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(planTiles.length, 3)}, 1fr)`, gap: 1.5 }}>
                            {planTiles.map(([plan, count]) => (
                              <StatCard key={plan} compact label={plan} value={count} sub={count === 1 ? 'user' : 'users'} />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                            No referred users yet — share your link to grow your business.
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                  </Box>
                  </Box>
                  {subscriptionDetails?.pending_reff_bonus_days ? (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {`${subscriptionDetails?.pending_reff_bonus_days} referral days available`}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={() => navigate(Pathname.plans)}>
                          {contentDashboard?.redeem || 'Redeem'}
                        </Button>
                      </Stack>
                    </>
                  ) : null}
                </SpotlightCard>

              {/* Viewers — one table, filter by Active / Pending.
                  Affiliates get a second column: the managed Users table. */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: isAffiliate ? { xs: '1fr', lg: '1fr 1fr' } : '1fr',
                gap: { xs: 2, md: 3 },
                alignItems: 'stretch',
              }}
              >
              {/* On the affiliate dashboard the Viewers column uses the same
                  simple PeopleColumn as the Affiliates column (identical CSS);
                  non-affiliates keep the richer single-column table below. */}
              {isAffiliate && (() => {
                const q = viewerSearch.trim().toLowerCase();
                const isViewing = viewerTab === 'viewing';
                // Viewers / Viewing segmented toggle shared by both affiliate and
                // non-affiliate dashboards. Rendered inside the PeopleColumn header.
                const tabToggle = (
                  <Stack
                    direction="row"
                    sx={{
                      bgcolor: 'action.hover',
                      borderRadius: 999,
                      p: '3px',
                      height: 37,
                      boxSizing: 'border-box',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {[
                      { key: 'viewers', label: 'Viewers' },
                      { key: 'viewing', label: 'Viewing' },
                    ].map((t) => (
                      <Button
                        key={t.key}
                        size="small"
                        disableElevation
                        onClick={() => setViewerTab(t.key)}
                        variant={viewerTab === t.key ? 'contained' : 'text'}
                        sx={{
                          borderRadius: 999,
                          minWidth: 0,
                          height: '100%',
                          px: 1.75,
                          py: 0,
                          fontSize: 12.5,
                          fontWeight: 600,
                          lineHeight: 1,
                          textTransform: 'none',
                          boxShadow: 'none',
                          color: viewerTab === t.key ? undefined : 'text.secondary',
                          transition: 'color 220ms ease, background-color 220ms ease, transform 220ms cubic-bezier(0.34,1.56,0.64,1)',
                          '&:active': { transform: 'scale(0.96)' },
                        }}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </Stack>
                );
                // Viewing tab: map the /access connections into PeopleColumn rows.
                // firstname carries the display name so the shared row renderer shows it.
                // Build the column props per tab, then render ONE always-mounted
                // PeopleColumn so switching never remounts the card (no blink). The
                // inner list cross-fades via contentKey; the card frame stays put.
                let columnProps;
                if (isViewing) {
                  const vrows = (q
                    ? viewingList.filter((c) => (
                      (c.display_name || c.firstname || c.email || '').toLowerCase().includes(q)
                      || (c.email || '').toLowerCase().includes(q)
                    ))
                    : viewingList
                  ).map((c) => ({
                    id: c.id,
                    email: c.email,
                    firstname: c.display_name || `${c.firstname || ''} ${c.lastname || ''}`.trim() || (c.email ? c.email.split('@')[0] : ''),
                    lastname: '',
                  }));
                  columnProps = {
                    title: 'Viewing',
                    subtitle: `${viewingList.length} ${viewingList.length === 1 ? 'projector' : 'projectors'} you can view`,
                    titleAction: (
                      <Tooltip title="Open access page">
                        <IconButton size="small" onClick={() => navigate(Pathname.access)} sx={PEOPLE_ICON_BTN_SX}>
                          <ExternalLink size={18} />
                        </IconButton>
                      </Tooltip>
                    ),
                    rows: vrows,
                    rowAction: (row) => (
                      <Tooltip title="Open projector">
                        <IconButton size="small" onClick={() => navigate(Pathname.getFriendProfilePath(row.id))} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                          <ExternalLink size={17} />
                        </IconButton>
                      </Tooltip>
                    ),
                    emptyText: viewerSearch ? `No projectors match “${viewerSearch.trim()}”` : 'You are not viewing any projectors yet',
                  };
                } else {
                  const allRows = [
                    ...((viewers || []).map((v) => ({ ...v, status: 'active' }))),
                    ...((pendingViewersList || []).map((v) => ({ ...v, status: 'pending' }))),
                  ];
                  const rows = q
                    ? allRows.filter((r) => (`${r.firstname || ''} ${r.lastname || ''}`.toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q)))
                    : allRows;
                  columnProps = {
                    title: 'Viewers',
                    subtitle: `${viewers?.length || 0} active · ${pendingViewersList?.length || 0} pending`,
                    // Both actions sit beside the title, matching Affiliates.
                    titleAction: (
                      <>
                        <Tooltip title={linkCopied ? 'Copied' : 'Copy invite link'}>
                          <IconButton size="small" onClick={copyProjectorLink} sx={PEOPLE_ICON_BTN_SX}>
                            {linkCopied ? <Check size={18} /> : <Copy size={18} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Invite viewer">
                          <IconButton size="small" onClick={() => navigate(Pathname.grantAccess)} sx={PEOPLE_ICON_BTN_SX}>
                            <UserPlus size={18} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ),
                    rows,
                    rowAction: (row, name) => {
                      const isPending = row.status === 'pending';
                      return (
                        <Tooltip title={isPending ? 'Cancel invite' : 'Remove viewer'}>
                          <IconButton size="small" onClick={() => (isPending ? handleCancelInvite(row.email) : handleDeleteViewer(row.id, name))} sx={{ color: isPending ? 'text.secondary' : 'error.main' }}>
                            {isPending ? <X size={17} /> : <Trash2 size={17} />}
                          </IconButton>
                        </Tooltip>
                      );
                    },
                    emptyText: viewerSearch ? `No viewers match “${viewerSearch.trim()}”` : 'No viewers yet',
                  };
                }
                return (
                  <PeopleColumn
                    search={viewerSearch}
                    onSearch={setViewerSearch}
                    titleNode={tabToggle}
                    contentKey={viewerTab}
                    {...columnProps}
                  />
                );
              })()}
              {!isAffiliate && (
              <Card sx={{ p: { xs: 2, md: 3 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, mb: 1.5 }}
                >
                  <Box>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{viewerTab === 'viewing' ? 'Viewing' : 'Viewers'}</Typography>
                      <Tooltip title={viewerTab === 'viewing' ? 'Open access page' : 'Open viewers page'}>
                        <IconButton
                          size="small"
                          aria-label={viewerTab === 'viewing' ? 'Open access page' : 'Open viewers page'}
                          onClick={() => navigate(viewerTab === 'viewing' ? Pathname.access : Pathname.viewers.basePath)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                            '& svg': { transition: 'transform 200ms ease' },
                            '&:hover svg': { transform: 'translate(1px, -1px)' },
                          }}
                        >
                          <ExternalLink size={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {viewerTab === 'viewing'
                        ? `${viewingList.length} ${viewingList.length === 1 ? 'projector' : 'projectors'} you can view`
                        : `${viewers?.length || 0} active · ${pendingViewersList?.length || 0} pending${storageDetails.allowedViewers ? ` · max ${storageDetails.allowedViewers}` : ''}`}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                    <PillSearch value={viewerSearch} onChange={setViewerSearch} placeholder={viewerTab === 'viewing' ? 'Search projectors' : 'Search viewers'} minWidth={180} />
                    {/* Viewers / Viewing top tabs. */}
                    <Stack direction="row" sx={{ bgcolor: 'action.hover', borderRadius: 999, p: 0.5 }}>
                      {[
                        { key: 'viewers', label: 'Viewers' },
                        { key: 'viewing', label: 'Viewing' },
                      ].map((t) => (
                        <Button
                          key={t.key}
                          size="small"
                          disableElevation
                          onClick={() => setViewerTab(t.key)}
                          variant={viewerTab === t.key ? 'contained' : 'text'}
                          sx={{
                            borderRadius: 999,
                            minWidth: 0,
                            px: 1.5,
                            textTransform: 'none',
                            color: viewerTab === t.key ? undefined : 'text.secondary',
                          }}
                        >
                          {t.label}
                        </Button>
                      ))}
                    </Stack>
                    {viewerTab === 'viewers' && (
                    <Stack direction="row" sx={{ bgcolor: 'action.hover', borderRadius: 999, p: 0.5 }}>
                      {[
                        { key: 'active', label: `Active · ${viewers?.length || 0}`, icon: <Users size={15} /> },
                        { key: 'pending', label: `Pending · ${pendingViewersList?.length || 0}`, icon: <Clock size={15} /> },
                      ].map((f) => (
                        <Button
                          key={f.key}
                          size="small"
                          disableElevation
                          onClick={() => setViewerFilter(f.key)}
                          variant={viewerFilter === f.key ? 'contained' : 'text'}
                          startIcon={f.icon}
                          sx={{
                            borderRadius: 999,
                            minWidth: 0,
                            px: 1.5,
                            textTransform: 'none',
                            color: viewerFilter === f.key ? undefined : 'text.secondary',
                          }}
                        >
                          {f.label}
                        </Button>
                      ))}
                    </Stack>
                    )}
                    {/* Copy link + Invite joined into one control. */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'stretch',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: '1px solid #d0d5dd',
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={copyProjectorLink}
                        startIcon={linkCopied ? <Check size={16} /> : <Copy size={16} />}
                        sx={{
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          borderRadius: 0,
                          fontWeight: 500,
                          color: '#15171c',
                          bgcolor: '#fff',
                          borderRight: '1px solid #e6e8ec',
                          px: 1.5,
                          '&:hover': { bgcolor: '#f4f5f7' },
                          '& svg': { transition: 'transform 220ms cubic-bezier(0.34,1.56,0.64,1)' },
                          '&:hover svg': { transform: 'translateY(-2px) scale(1.12)' },
                        }}
                      >
                        {linkCopied ? 'Copied' : 'Copy link'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={() => navigate(Pathname.grantAccess)}
                        startIcon={<UserPlus size={16} />}
                        sx={{
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          borderRadius: 0,
                          fontWeight: 500,
                          px: 1.75,
                          '& svg': { transition: 'transform 220ms cubic-bezier(0.34,1.56,0.64,1)' },
                          '&:hover svg': { transform: 'translateX(2px) scale(1.12)' },
                        }}
                      >
                        Invite
                      </Button>
                    </Box>
                  </Stack>
                </Stack>

                <Box sx={{ maxHeight: 420, overflowY: 'auto', overflowX: 'auto', mt: 1 }}>
                  {(() => {
                    // Viewing tab: the projectors I can view (same data /access shows).
                    if (viewerTab === 'viewing') {
                      const vq = viewerSearch.trim().toLowerCase();
                      const vrows = vq
                        ? viewingList.filter((c) => (
                          (c.display_name || c.firstname || c.email || '').toLowerCase().includes(vq)
                          || (c.email || '').toLowerCase().includes(vq)
                        ))
                        : viewingList;
                      if (!vrows.length) {
                        return (
                          <Stack spacing={1} sx={{ alignItems: 'center', py: 5, color: 'text.secondary' }}>
                            {vq ? <Search size={28} /> : <ExternalLink size={28} />}
                            <Typography variant="body2">
                              {vq ? `No projectors match “${viewerSearch.trim()}”` : 'You are not viewing any projectors yet'}
                            </Typography>
                            {!vq && (
                              <Button onClick={() => navigate(Pathname.access)} size="small" variant="contained" disableElevation startIcon={<ExternalLink size={16} />} sx={{ mt: 1, textTransform: 'none' }}>
                                Browse access
                              </Button>
                            )}
                          </Stack>
                        );
                      }
                      return (
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                          <Box component="thead">
                            <Box
                              component="tr"
                              sx={{ '& th': { textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } }}
                            >
                              <Box component="th">Projector</Box>
                              <Box component="th" sx={{ textAlign: 'right !important' }}>Action</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {vrows.map((c) => {
                              const vname = c.display_name || `${c.firstname || ''} ${c.lastname || ''}`.trim() || c.email?.split('@')[0] || '—';
                              return (
                                <Box
                                  component="tr"
                                  key={c.id || c.email}
                                  sx={{ '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: 'action.hover' } }}
                                >
                                  <Box component="td">
                                    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                                      {c.image ? (
                                        <Box component="img" src={c.image} alt="" sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                      ) : (
                                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase' }}>
                                          {vname.charAt(0)}
                                        </Box>
                                      )}
                                      <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{vname}</Typography>
                                    </Stack>
                                  </Box>
                                  <Box component="td" sx={{ textAlign: 'right' }}>
                                    <Tooltip title="Open projector">
                                      <IconButton size="small" onClick={() => navigate(Pathname.getFriendProfilePath(c.id))} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                                        <ExternalLink size={17} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    }
                    const base = viewerFilter === 'active'
                      ? (viewers || []).map((v) => ({ ...v, status: 'active' }))
                      : (pendingViewersList || []).map((v) => ({ ...v, status: 'pending' }));
                    const q = viewerSearch.trim().toLowerCase();
                    const rows = q
                      ? base.filter((v) => (
                        `${v.firstname || ''} ${v.lastname || ''}`.toLowerCase().includes(q)
                        || (v.email || '').toLowerCase().includes(q)
                      ))
                      : base;
                    if (!rows.length) {
                      return (
                        <Stack spacing={1} sx={{ alignItems: 'center', py: 5, color: 'text.secondary' }}>
                          {q ? <Search size={28} /> : (viewerFilter === 'active' ? <Users size={28} /> : <Clock size={28} />)}
                          <Typography variant="body2">
                            {q
                              ? `No viewers match “${viewerSearch.trim()}”`
                              : (viewerFilter === 'active'
                                ? (contentDashboard?.noViewers || 'No active viewers yet')
                                : (contentDashboard?.noNewReq || 'No pending requests'))}
                          </Typography>
                          {!q && viewerFilter === 'active' && (
                            <Button onClick={() => navigate(Pathname.grantAccess)} size="small" variant="contained" disableElevation startIcon={<UserPlus size={16} />} sx={{ mt: 1, textTransform: 'none' }}>
                              Invite a viewer
                            </Button>
                          )}
                        </Stack>
                      );
                    }
                    return (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead">
                          <Box
                            component="tr"
                            sx={{ '& th': { textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 } }}
                          >
                            <Box component="th">Name</Box>
                            {!isAffiliate && <Box component="th">Email</Box>}
                            {!isAffiliate && <Box component="th">Status</Box>}
                            <Box component="th" sx={{ textAlign: 'right !important' }}>Action</Box>
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {rows.map((row) => {
                            const name = `${row.firstname || ''} ${row.lastname || ''}`.trim() || row.email?.split('@')[0] || '—';
                            const isPending = row.status === 'pending';
                            return (
                              <Box
                                component="tr"
                                key={row.id || row.email}
                                sx={{ '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: 'action.hover' } }}
                              >
                                <Box component="td">
                                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase' }}>
                                      {name.charAt(0)}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{name}</Typography>
                                      {/* In the cramped affiliate 2-column layout the email
                                          sits under the name instead of its own column. */}
                                      {isAffiliate && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{row.email}</Typography>
                                      )}
                                    </Box>
                                  </Stack>
                                </Box>
                                {!isAffiliate && (
                                  <Box component="td">
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>{row.email}</Typography>
                                  </Box>
                                )}
                                {!isAffiliate && (
                                  <Box component="td">
                                    <Chip
                                      size="small"
                                      label={isPending ? 'Pending' : 'Active'}
                                      color={isPending ? 'warning' : 'success'}
                                      variant="outlined"
                                      sx={{ fontWeight: 600, height: 22 }}
                                    />
                                  </Box>
                                )}
                                <Box component="td" sx={{ textAlign: 'right' }}>
                                  <Tooltip title={isPending ? 'Cancel invite' : 'Remove viewer'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => (isPending ? handleCancelInvite(row.email) : handleDeleteViewer(row.id, name))}
                                      sx={{ color: isPending ? 'text.secondary' : 'error.main' }}
                                    >
                                      {isPending ? <X size={17} /> : <Trash2 size={17} />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </Card>
              )}
              {isAffiliate && (() => {
                const aq = affiliateSearch.trim().toLowerCase();
                const rows = aq
                  ? affiliateUsers.filter((r) => (`${r.firstname || ''} ${r.lastname || ''}`.toLowerCase().includes(aq) || (r.email || '').toLowerCase().includes(aq)))
                  : affiliateUsers;
                return (
                  <PeopleColumn
                    title="Affiliate Users"
                    subtitle={`${affiliateUsers.length} ${affiliateUsers.length === 1 ? 'user' : 'users'}`}
                    search={affiliateSearch}
                    onSearch={setAffiliateSearch}
                    // Copy affiliate link sits beside the title, not by the search.
                    titleAction={(
                      <Tooltip title={affiliateLinkCopied ? 'Copied' : 'Copy affiliate link'}>
                        <IconButton size="small" onClick={copyAffiliateLink} sx={PEOPLE_ICON_BTN_SX}>
                          {affiliateLinkCopied ? <Check size={18} /> : <Copy size={18} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    rows={rows}
                    rowAction={(row, name) => (row?.userId && row?.token ? (
                      <Tooltip title={`Access ${name}'s account`}>
                        <IconButton
                          size="small"
                          onClick={() => setAccessTarget({ row, name })}
                          sx={PEOPLE_ICON_BTN_SX}
                        >
                          <ArrowUpRight size={18} />
                        </IconButton>
                      </Tooltip>
                    ) : null)}
                    emptyText={affiliateSearch ? `No users match “${affiliateSearch.trim()}”` : 'No users yet'}
                    loading={affiliateLoading}
                  />
                );
              })()}
              </Box>
            </Stack>

            {/* Payment-attention overlay: keep the dashboard visible but blurred
                behind a single centered object, instead of replacing the whole
                dashboard. Sits below the header (1200) + dock (1250) so nav
                stays usable; body scroll is locked while it's up. */}
            {isInactiveAttention && (
              <Box
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 1100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  backgroundColor: 'rgba(2,6,23,0.35)',
                }}
              >
                <Card
                  sx={{
                    p: { xs: 3, md: 4 },
                    maxWidth: 460,
                    width: '100%',
                    textAlign: 'center',
                    borderColor: 'error.main',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      mx: 'auto',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'error.main',
                      color: '#fff',
                    }}
                  >
                    <Box
                      component="svg"
                      width="26"
                      height="26"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, mb: 1, color: 'error.main', fontSize: 18 }}>
                    {contentDashboard?.needYourAttention || 'Need your attention'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Your payment could not be processed. Please contact billing@projectorstream.com or update your payment method to resume billing.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(subscriptionDetails?.paymentMethods?.length > 0 ? '/settings/billings-and-payments' : '/settings/manage-payment-methods?add=true')}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Resume billing
                  </Button>
                </Card>
              </Box>
            )}
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        {...snackbar}
        onClose={() => setSnackbar({ ...snackbar, isVisible: false })}
      />

      <ConfirmDialog
        open={Boolean(accessTarget)}
        icon={<ArrowUpRight size={22} />}
        title={accessTarget?.name ? `Access ${accessTarget.name}’s account?` : 'Access this account?'}
        description="You’ll switch into this account and act on their behalf until you exit."
        confirmLabel="Access account"
        cancelLabel="Cancel"
        loading={accessing}
        onConfirm={confirmAccess}
        onCancel={() => setAccessTarget(null)}
      />

      {showListingModal && (
        <ListingModal
          open={showListingModal}
          setOpen={() => setShowListingModal(false)}
          videoFiles={videoFiles}
          setVideoFiles={setVideoFiles}
          buttonText="Upload Videos"
          setButtonText={() => {}}
          setEditLoader={() => {}}
        />
      )}

      <UploadModal
        isVisible={showVideoDetailModal}
        onHide={() => {
          setShowVideoDetailModal(false);
          setSingleVideoFile(null);
        }}
        imageFiles={singleVideoFile ? [singleVideoFile] : []}
      />

      <MobileUploadQRModal
        open={mobileQROpen}
        onClose={() => setMobileQROpen(false)}
      />

      <CloudUploadModal
        open={Boolean(cloudService)}
        onClose={() => setCloudService(null)}
        service={cloudService}
        onFilesSelected={(files, svc) => {
          // Hand the selected files to the upload pipeline by stashing in
          // sessionStorage. UploadDropdown picks these up on next mount and
          // runs its full cloud download/upload flow (see UploadDropdown.jsx
          // useEffect that reads pendingCloudFiles).
          try {
            sessionStorage.setItem(
              'pendingCloudFiles',
              JSON.stringify({ files, service: svc }),
            );
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to stash pending cloud files', e);
          }
          setCloudService(null);
          navigate(Pathname.yourVideos);
        }}
        isUploading={false}
        onShowMessage={(message) => setSnackbar({ isVisible: true, message, type: 'default' })}
      />
    </Page>
  );
};

/**
 * Accessing a managed user from the affiliate/Users table ON THIS PAGE lands on
 * /dashboard — the route that is already mounted. React Router keeps the same
 * element alive, so DashboardInner never remounts: its mount-only work (viewer
 * + access-request lists, the referral/managed-users lookup, the Firebase
 * listener) keeps showing the manager's data, and only a tab refocus — SWR's
 * revalidateOnFocus — quietly swapped it for the accessed user's.
 *
 * Keying the tree on the account being driven forces a clean remount the
 * instant handleClick swaps the token, so every fetch re-runs against the new
 * account. `userAccessedData` is set/cleared synchronously on switch in/out
 * (app.jsx handleClick/handleManger), which is why it — rather than the
 * asynchronously-loaded profile id — is the key.
 */
export const DashboardV2 = () => {
  const { userAccessedData } = useContext(AppContext);
  const accountKey = String(
    userAccessedData?.userId || userAccessedData?.id || 'self'
  );
  return (
    <V2ThemeProvider>
      <DashboardInner key={accountKey} />
    </V2ThemeProvider>
  );
};
